import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import InviteUserModal from "./modals/InviteUserModal";
import tournamentService from "../../services/api/tournamentService";

// Import custom hooks
import { useTeamData } from "./hooks/useTeamData";
import { useTeamModals } from "./hooks/useTeamModals";
import { useTeamActions } from "./hooks/useTeamActions";

// Import tab components
import TeamHeader from "./TeamHeader";
import TeamOverviewTab from "./tabs/TeamOverviewTab";
import TeamMembersTab from "./tabs/TeamMembersTab";
import TeamRequestsTab from "./tabs/TeamRequestsTab";
import TeamStatsTab from "./tabs/TeamStatsTab";
import TeamModals from "./modals/TeamModals";
import { TEAM_MEMBER_ADMIN_ROLES, USER_ROLES, normalizeUserRole } from "../../constants/roles";

// Import styles
import "../../styles/features/teams.css";
import teamService from "../../services/api/teamService";

/**
 * Refactored TeamDetails Container Component
 * Main orchestrator for team details view with modular sub-components
 */
const TeamDetailsContainer = ({
  teamId,
  currentUser,
  token,
  onLogout,
  onBack,
  onJoinRequest,
  returnTo = "browse",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTabAppliedRef = useRef(false);
  const fetchedTabsRef = useRef(new Set()); // Track which tabs have been fetched

  // Local state
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || "overview");
  const [successMessage, setSuccessMessage] = useState("");

  // Initialize tab from location state
  useEffect(() => {
    if (!initialTabAppliedRef.current && location.state?.initialTab) {
      setActiveTab(location.state.initialTab);
      initialTabAppliedRef.current = true;
    }
  }, [location.state?.initialTab]);

  // Validate teamId — accepts integer, UUID v4, or 7-digit short code
  const isValidTeamId = teamId && (
    /^\d+$/.test(teamId.toString()) ||                                                          // integer
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(teamId.toString()) || // UUID v4
    /^\d{7}$/.test(teamId.toString())                                                            // 7-digit short code
  );

  useEffect(() => {
    if (!isValidTeamId) {
      console.warn("TeamDetails received invalid teamId:", teamId, "- navigating back");
      navigate(returnTo === "my-teams" ? "/teams/my-teams" : "/teams/browse", { replace: true });
    }
  }, [teamId, isValidTeamId, returnTo, navigate]);

  // Use custom hooks
  const teamData = useTeamData(teamId, currentUser, isValidTeamId);
  const modals = useTeamModals();
  const actions = useTeamActions(teamId, teamData.team, currentUser, teamData.userMembership, teamData.fetchTeamData);

  // Derived state
  const canManageMembers = currentUser &&
    (Number(teamData.team?.created_by) === Number(currentUser.id) ||
      (teamData.userMembership && TEAM_MEMBER_ADMIN_ROLES.includes(teamData.userMembership.role?.toLowerCase())));
  // The logo: the team's managers and its captain (as on the server)
  const canChangeLogo = Boolean(canManageMembers ||
    (teamData.userMembership?.status !== 'inactive' && teamData.userMembership?.role?.toLowerCase() === 'captain'));
  // Handing out roles (scorer, captain, manager...) is owner/admin only on the server.
  const canAssignRoles = currentUser &&
    (Number(teamData.team?.created_by) === Number(currentUser.id) ||
      [USER_ROLES.SUPER_ADMIN, USER_ROLES.PLATFORM_ADMIN].includes(normalizeUserRole(currentUser.role)));

  const getActiveMembersByType = (type) => {
    return Array.isArray(teamData.members)
      ? teamData.members.filter((m) => (m.member_type || "primary") === type && m.status === "active")
      : [];
  };

  // Fetch stats when tab changes - only once per team per session
  useEffect(() => {
    const tabKey = `stats-${teamId}`;
    if (activeTab === "stats" && !fetchedTabsRef.current.has(tabKey)) {
      fetchedTabsRef.current.add(tabKey);
      teamData.fetchTeamStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, teamId]);

  // Fetch schedule matches when tab changes - only once per team per session
  useEffect(() => {
    const tabKey = `overview-${teamId}`;
    if (activeTab === "overview" && !fetchedTabsRef.current.has(tabKey)) {
      fetchedTabsRef.current.add(tabKey);
      teamData.fetchScheduleMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, teamId]);

  // Handlers - Join/Leave
  const handleJoinTeam = async () => {
    const status = await actions.handleJoinTeam(teamData.setError, setSuccessMessage);
    if (status) {
      teamData.setJoinRequestStatus(status);
      if (onJoinRequest) {
        onJoinRequest(teamId);
      }
    }
  };

  const handleLeaveTeam = () => {
    if (actions.handleLeaveTeam(teamData.setError)) {
      modals.setShowLeaveConfirm(true);
    }
  };

  const confirmLeaveTeam = async () => {
    await actions.confirmLeaveTeam(
      teamData.setError,
      setSuccessMessage,
      modals.setShowLeaveConfirm,
      modals.setIsLeaving
    );
    teamData.setUserMembership(null);
    modals.setIsLeaving(false);
  };

  // Handlers - Approvals/Rejections
  const handleApproveRequest = (userId, username) => {
    actions.handleApproveRequest(
      userId,
      username,
      modals.setApproveTarget,
      modals.setSelectedMemberType,
      modals.setShowApproveModal
    );
  };

  const confirmApproveRequest = async () => {
    await actions.confirmApproveRequest(
      modals.approveTarget,
      modals.selectedMemberType,
      teamData.setError,
      setSuccessMessage,
      modals.setShowApproveModal,
      modals.setIsChangingMemberType
    );
  };

  const handleRejectRequest = (userId, username) => {
    actions.handleRejectRequest(userId, username, modals.setRejectTarget, modals.setShowRejectConfirm);
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await tournamentService.respondToTeamInvitation(invitationId, "accept");
      setSuccessMessage("✅ Invitation accepted!");
      teamData.setTournamentInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } catch (err) {
      teamData.setError(err.message || "Failed to accept invitation");
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await tournamentService.respondToTeamInvitation(invitationId, "decline");
      setSuccessMessage("Invitation declined.");
      teamData.setTournamentInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } catch (err) {
      teamData.setError(err.message || "Failed to decline invitation");
    }
  };

  const confirmRejectRequest = async () => {
    await actions.confirmRejectRequest(
      modals.rejectTarget,
      teamData.setError,
      setSuccessMessage,
      modals.setShowRejectConfirm,
      modals.setRejectTarget
    );
  };

  // Handlers - Member Management
  const handleRemoveMember = (userId, userName) => {
    actions.handleRemoveMember(userId, userName, modals.setRemoveMemberTarget, modals.setShowRemoveMemberConfirm);
  };

  const confirmRemoveMember = async () => {
    await actions.confirmRemoveMember(
      modals.removeMemberTarget,
      teamData.setError,
      modals.setIsRemovingMember,
      modals.setShowRemoveMemberConfirm,
      setSuccessMessage,
      modals.setRemoveMemberTarget
    );
  };

  // Handlers - Scorer toggle
  const handleToggleScorer = async (member) => {
    const newRole = member.role === "scorer" ? "player" : "scorer";
    try {
      await teamService.updateTeamMember(teamId, member.user_id, { role: newRole });
      // Optimistically update the local member list immediately for instant UI feedback
      teamData.setMembers((prev) =>
        prev.map((m) => m.user_id === member.user_id ? { ...m, role: newRole } : m)
      );
      setSuccessMessage(
        newRole === "scorer"
          ? `${member.first_name} ${member.last_name} is now a Scorer`
          : `Scorer role removed from ${member.first_name} ${member.last_name}`
      );
    } catch (err) {
      teamData.setError(`Failed to update scorer role: ${err.message}`);
    }
  };

  // Handlers - Move member between starter/substitute rosters.
  // If the target roster is full, open a swap-partner picker instead of erroring.
  const handleOpenSwapModal = async (member, targetType) => {
    const targetLabel = targetType === "primary" ? "Starting Roster" : "Substitutes";
    const currentInTarget = getActiveMembersByType(targetType);
    const team = teamData.team;

    const maxPrimary = team?.max_members ?? team?.settings?.max_members ?? 999;
    const maxSubs    = team?.max_substitutes ?? team?.settings?.max_substitutes ?? team?.settings?.rules?.substitutions ?? 999;
    const limit      = targetType === "primary" ? maxPrimary : maxSubs;

    if (currentInTarget.length >= limit) {
      // Roster is full — open the swap picker so user can choose who to exchange with
      if (currentInTarget.length === 0) {
        teamData.setError(`No ${targetLabel} members to swap with.`);
        return;
      }
      modals.setSwapSource({
        userId: member.user_id,
        name: `${member.first_name} ${member.last_name}`,
        targetType,
        currentType: member.member_type || "primary",
        member,
      });
      modals.setSwapTargetUserId(String(currentInTarget[0].user_id));
      modals.setShowSwapModal(true);
      return;
    }

    // Target has room — direct move
    try {
      teamData.setError("");
      setSuccessMessage("");
      modals.setIsChangingMemberType(true);
      await teamService.changeMemberType(teamId, member.user_id, targetType, null);
      const name = `${member.first_name} ${member.last_name}`;
      setSuccessMessage(`Moved ${name} to ${targetLabel}.`);
      setTimeout(() => setSuccessMessage(""), 5000);
      await teamData.fetchTeamData();
    } catch (err) {
      console.error("Error moving member:", err);
      teamData.setError(err.response?.data?.message || "Failed to move member. Please try again.");
    } finally {
      modals.setIsChangingMemberType(false);
    }
  };

  // Confirm swap: exchange member_type between two players
  const confirmSwapMemberType = async () => {
    if (!modals.swapSource || !modals.swapTargetUserId) return;
    try {
      teamData.setError("");
      setSuccessMessage("");
      modals.setIsChangingMemberType(true);

      // Swap: move source to targetType AND move target to source's current type
      const sourceType = modals.swapSource.targetType;       // where source is going
      const targetType = modals.swapSource.currentType;      // where target is going

      await teamService.changeMemberType(teamId, modals.swapSource.userId, sourceType, parseInt(modals.swapTargetUserId, 10));

      setSuccessMessage(`Swapped ${modals.swapSource.name} successfully.`);
      setTimeout(() => setSuccessMessage(""), 5000);
      modals.setShowSwapModal(false);
      await teamData.fetchTeamData();
    } catch (err) {
      console.error("Error swapping members:", err);
      teamData.setError(err.response?.data?.message || "Failed to swap members. Please try again.");
    } finally {
      modals.setIsChangingMemberType(false);
    }
  };

  // Handlers - Team edit/delete
  const handleEditTeam = () => {
    navigate(`/teams/${teamId}/edit`);
  };

  const handleDeleteTeam = () => {
    if (!currentUser) {
      teamData.setError("You must be logged in to delete this team.");
      return;
    }
    modals.setShowDeleteConfirm(true);
  };

  const confirmDeleteTeam = async () => {
    try {
      teamData.setError("");
      modals.setIsDeleting(true);
      modals.setShowDeleteConfirm(false);
      await teamService.deleteTeam(teamId);

      if (onBack) {
        onBack();
      } else {
        window.location.href = "/teams";
      }
    } catch (err) {
      console.error("Error deleting team:", err);
      teamData.setError("Failed to delete team. Please try again.");
    } finally {
      modals.setIsDeleting(false);
    }
  };

  // Render states
  if (!isValidTeamId) {
    return null;
  }

  if (teamData.loading) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-management-gaming">
          <FloatingCard className="loading-card-gaming delay-1">
            <div className="loading-content-gaming">
              <div className="gaming-spinner"></div>
              <p className="loading-text">Loading team details...</p>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  if (teamData.error && !teamData.team) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-management-gaming">
          <FloatingCard className="error-card-gaming delay-1">
            <div className="error-content-gaming">
              <span className="error-icon">❌</span>
              <h3 className="error-title">Error Loading Team</h3>
              <p className="error-message">{teamData.error}</p>
              <FloatingButton variant="primary" onClick={onBack} icon="←">
                <span className="btn-text">Go Back</span>
              </FloatingButton>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  if (!teamData.team) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-management-gaming">
          <FloatingCard className="error-card-gaming delay-1">
            <div className="error-content-gaming">
              <span className="error-icon">🚫</span>
              <h3 className="error-title">Team Not Found</h3>
              <p className="error-message">
                The team you're looking for doesn't exist or has been removed.
              </p>
              <FloatingButton variant="primary" onClick={onBack} icon="←">
                <span className="btn-text">Go Back</span>
              </FloatingButton>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={currentUser}
      showFloatingNav={true}
    >
      <div className="team-management-gaming">
        {/* Team Header */}
        <TeamHeader
          team={teamData.team}
          currentUser={currentUser}
          userMembership={teamData.userMembership}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onBack={onBack}
          onLeaveTeam={handleLeaveTeam}
          onEditTeam={handleEditTeam}
          onDeleteTeam={handleDeleteTeam}
          onInviteClick={() => modals.setShowInviteModal(true)}
          onJoinClick={handleJoinTeam}
          joinRequestStatus={teamData.joinRequestStatus}
          canManageMembers={canManageMembers}
          canChangeLogo={canChangeLogo}
          onLogoChange={(logoUrl) => teamData.setTeam((prev) => (prev ? { ...prev, logo_url: logoUrl } : prev))}
          returnTo={returnTo}
          members={teamData.members}
          joinRequests={teamData.joinRequests}
          tournamentInvitations={teamData.tournamentInvitations}
          successMessage={successMessage}
          error={teamData.error}
          onClearError={() => teamData.setError("")}
        />

        {/* Tab Content */}
        <div className="tab-content-gaming">
          {activeTab === "overview" && (
            <TeamOverviewTab
              team={teamData.team}
              rosterSummary={teamData.rosterSummary}
              scheduleMatches={teamData.scheduleMatches}
              loadingMatches={teamData.loadingMatches}
            />
          )}

          {activeTab === "members" && (
            <TeamMembersTab
              members={teamData.members}
              canManageMembers={canManageMembers}
              isChangingMemberType={modals.isChangingMemberType}
              getActiveMembersByType={getActiveMembersByType}
              onSwapMember={handleOpenSwapModal}
              onRemoveMember={handleRemoveMember}
              onToggleScorer={canAssignRoles ? handleToggleScorer : undefined}
              ownerId={teamData.team?.created_by}
              onTransferOwnership={
                currentUser && Number(teamData.team?.created_by) === Number(currentUser.id)
                  ? (member) => actions.transferOwnership(member, teamData.setError, setSuccessMessage)
                  : undefined
              }
            />
          )}

          {/* Managers/coaches review join requests too (TM-B25) */}
          {activeTab === "requests" && canManageMembers && (
            <TeamRequestsTab
              joinRequests={teamData.joinRequests}
              onApproveRequest={handleApproveRequest}
              onRejectRequest={handleRejectRequest}
              tournamentInvitations={teamData.tournamentInvitations}
              onAcceptInvitation={handleAcceptInvitation}
              onDeclineInvitation={handleDeclineInvitation}
            />
          )}

          {activeTab === "stats" && (
            <TeamStatsTab
              teamStatsSummary={teamData.teamStatsSummary}
              stats={teamData.stats}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <TeamModals
        showLeaveConfirm={modals.showLeaveConfirm}
        showRejectConfirm={modals.showRejectConfirm}
        showApproveModal={modals.showApproveModal}
        showDeleteConfirm={modals.showDeleteConfirm}
        showRemoveMemberConfirm={modals.showRemoveMemberConfirm}
        showSwapModal={modals.showSwapModal}
        isLeaving={modals.isLeaving}
        isDeleting={modals.isDeleting}
        isRemovingMember={modals.isRemovingMember}
        isChangingMemberType={modals.isChangingMemberType}
        rejectTarget={modals.rejectTarget}
        approveTarget={modals.approveTarget}
        removeMemberTarget={modals.removeMemberTarget}
        swapSource={modals.swapSource}
        swapTargetUserId={modals.swapTargetUserId}
        selectedMemberType={modals.selectedMemberType}
        rosterSummary={teamData.rosterSummary}
        getActiveMembersByType={getActiveMembersByType}
        onLeaveConfirm={confirmLeaveTeam}
        onLeaveCancel={() => modals.setShowLeaveConfirm(false)}
        onRejectConfirm={confirmRejectRequest}
        onRejectCancel={() => {
          modals.setShowRejectConfirm(false);
          modals.setRejectTarget(null);
        }}
        onApproveConfirm={confirmApproveRequest}
        onApproveCancel={() => modals.setShowApproveModal(false)}
        onApproveTypeChange={modals.setSelectedMemberType}
        onDeleteConfirm={confirmDeleteTeam}
        onDeleteCancel={() => modals.setShowDeleteConfirm(false)}
        onRemoveMemberConfirm={confirmRemoveMember}
        onRemoveMemberCancel={() => {
          modals.setShowRemoveMemberConfirm(false);
          modals.setRemoveMemberTarget(null);
        }}
        onSwapConfirm={confirmSwapMemberType}
        onSwapCancel={() => modals.setShowSwapModal(false)}
        onSwapTargetChange={modals.setSwapTargetUserId}
      />

      {/* Invite Modal */}
      {modals.showInviteModal && (
        <InviteUserModal
          team={teamData.team}
          currentUser={currentUser}
          onClose={() => modals.setShowInviteModal(false)}
          onInviteSuccess={() => {
            teamData.fetchTeamData();
          }}
        />
      )}
    </GameLayout>
  );
};

export default TeamDetailsContainer;
